import { Injectable } from "@angular/core";
import { AuthService } from "../../auth/auth.service";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpInterceptor,
} from "@angular/common/http";
import { Observable, throwError, from, ReplaySubject, EMPTY, of } from "rxjs";
import { catchError, mergeMap, finalize, tap, map } from "rxjs/operators";
import { TimeService } from "../../time/time.service";
import { RefreshTokenConfigurationService } from "../../configuration/auth/refresh-token/refresh-token-configuration.service";
import { RefreshTokenGrantService } from "../../auth/grants/refresh-token-grant/refresh-token-grant.service";
import { Router, ParamMap } from "@angular/router";
import { AuthConfigurationService } from "../../configuration/auth/auth-configuration.service";
import { LogoutService } from '../../auth/logout.service';

@Injectable()
export class AuthHttpInterceptorService implements HttpInterceptor {
  authorizeResponseQueryParams = ["code", "error"];

  get authConfiguration() {
    return this.authConfigurationService.configuration;
  }

  get refreshTokenConfiguration() {
    return this.refreshTokenConfigurationService.configuration;
  }

  get refreshTokenEnabled() {
    return this.refreshTokenConfiguration.enabled;
  }

  get authConfigurationLoaded() {
    return this.authConfigurationService.loaded;
  }

  get refresh_token_period() {
    return this.refreshTokenConfiguration.refresh_period;
  }

  get storeRefreshTokens() {
    return this.refreshTokenConfiguration.storeRefreshTokens;
  }

  get access_token_expires_in_offset() {
    if (this.authConfiguration.access_token_expires_in_offset) {
      return this.authConfiguration.access_token_expires_in_offset;
    } else {
      return 0;
    }
  }
  private refreshTokenInProgress = false;
  private refreshTokenReplay = new ReplaySubject<any>();

  constructor(
    private authService: AuthService,
    private timeService: TimeService,
    private refreshTokenConfigurationService: RefreshTokenConfigurationService,
    private refreshTokenService: RefreshTokenGrantService,
    private router: Router,
    private authConfigurationService: AuthConfigurationService,
    private logoutService: LogoutService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.authConfigurationLoaded) {
      if (
        this.loadToken() &&
        this.loadExpiresIn() &&
        this.loadTokenStoreTime()
      ) {
        const now = this.now();
        const expiryTime = this.workoutTokenExpiryTime();

        // If expiration is within offset, it will force renew
        if (now > expiryTime - this.access_token_expires_in_offset) {
          if (this.refreshTokenEnabled) {
            if (this.isRefreshTokenRequest(req)) {
              return next.handle(req);
            } else {
              if (this.refreshTokenInProgress) {
                return this.refreshTokenReplay.pipe(
                  mergeMap(() => {
                    return this.tokenState(req, next);
                  }),
                  catchError(e => {
                    return this.handleRefreshTokenAuthTokenError(req, next, e);
                  })
                );
              } else {
                return this.refreshTokenState().pipe(
                  mergeMap(() => {
                    return this.tokenState(req, next);
                  }),
                  catchError(e => {
                    return this.handleRefreshTokenAuthTokenError(req, next, e);
                  })
                );
              }
            }
          } else {
            if (this.isTokenRequest(req)) {
              return next.handle(req);
            } else {
              if (this.refreshTokenInProgress) {
                return this.refreshTokenReplay.pipe(
                  mergeMap(() => {
                    return this.tokenState(req, next);
                  }),
                  catchError((e) => {
                    return this.handleSilentAuthTokenError(req, next, e);
                  })
                );
              } else {
                return this.renewTokenState().pipe(
                  mergeMap(() => {
                    return this.tokenState(req, next);
                  }),
                  catchError((e) => {
                    return this.handleSilentAuthTokenError(req, next, e);
                  })
                );
              }
            }
          }
        } else {
          return this.tokenState(req, next).pipe(
            catchError((e) => {
              if (this.refreshTokenEnabled) {
                return this.handleRefreshTokenAuthTokenError(req, next, e);
              } else {
                return this.handleSilentAuthTokenError(req, next, e);
              }
            })
          );
        }
      } else if (this.loadToken()) {
        return this.tokenState(req, next).pipe(
          catchError((e) => {
            if (this.refreshTokenEnabled) {
              return this.handleRefreshTokenAuthTokenError(req, next, e);
            } else {
              return this.handleSilentAuthTokenError(req, next, e);
            }
          })
        );
      } else {
        return next.handle(req);
      }
    } else {
      return next.handle(req);
    }
  }

  isTokenRequest(req) {
    const pkceTokenUrl = this.createPkceAuthorizationCodeRequestUrl();
    return req.url.indexOf(pkceTokenUrl) > -1;
  }

  createPkceAuthorizationCodeRequestUrl() {
    return this.authService.createPkceAuthorizationCodeRequestUrl();
  }

  createPkceAuthorizationRequestUrl(codeChallenge) {
    return this.authService.createPkceAuthorizationRequestUrl(codeChallenge);
  }

  renewTokenState() {
    this.refreshTokenInProgress = true;
    this.refreshTokenReplay = new ReplaySubject<any>();
    return from(this.acquireTokenSilent()).pipe(
      tap(this.refreshTokenReplay),
      finalize(() => {
        this.refreshTokenInProgress = false;
      })
    );
  }

  refreshTokenState() {
    this.refreshTokenReplay = new ReplaySubject<any>();
    this.refreshTokenInProgress = true;
    return this.postRefreshTokenRequest().pipe(
      tap((data: any) => {
        if (data.access_token) {
          this.storeToken(data.access_token);
          this.storeTokenStoreTime(this.now());
        }
        if (data.expires_in) {
          this.storeExpiresIn(data.expires_in);
        }
        if (this.storeRefreshTokens && data.refresh_token) {
          this.storeRefreshToken(data.refresh_token);
        }
      }),
      tap(this.refreshTokenReplay),
      finalize(() => {
        this.refreshTokenInProgress = false;
      })
    );
  }

  clearAccessToken() {
    this.authService.clearToken();
  }

  clearAccessTokenStoreTime() {
    this.authService.clearTokenStoreTime();
  }

  clearTokenExpiresIn() {
    this.authService.clearExpiresIn();
  }

  isRefreshTokenRequest(req) {
    const url = req.url;
    const refreshTokenUrl = this.createRefreshTokenRequestUrl();
    return url.indexOf(refreshTokenUrl) !== -1;
  }

  createRefreshTokenRequestUrl() {
    return this.authService.createRefreshTokenRequestUrl();
  }

  storeRefreshToken(refreshToken) {
    this.authService.storeRefreshToken(refreshToken);
  }

  storeExpiresIn(expires_in) {
    return this.authService.storeExpiresIn(expires_in);
  }

  storeTokenStoreTime(time) {
    return this.authService.storeTokenStoreTime(time);
  }

  workoutTokenExpiryTime() {
    if (this.refreshTokenEnabled) {
      return (
        this.parseTime(this.loadTokenStoreTime()) + this.refresh_token_period
      );
    } else {
      return (
        this.parseTime(this.loadTokenStoreTime()) +
        this.parseTime(this.loadExpiresIn())
      );
    }
  }

  postRefreshTokenRequest() {
    return this.refreshTokenService.postRequest();
  }

  private tokenState(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const authHeader = this.createAuthHeader();
    const clone = req.clone({
      setHeaders: {
        ...authHeader,
      },
    });
    return next.handle(clone);
  }

  parseTime(time) {
    return Number.parseInt(time, 10);
  }

  createAuthHeader() {
    return this.authService.createAuthHeader();
  }

  storeToken(token) {
    this.authService.storeToken(token);
  }

  clearAuthData() {
    return this.authService.clearData();
  }

  now() {
    return this.timeService.now();
  }

  acquireTokenSilent() {
    const codeChallenge = this.loadCodeChallenge();
    if (codeChallenge) {
      const url = this.createAuthorizationRequestUrl(codeChallenge, "none");
      return this.renewTokenInIframe(url);
    } else {
      throw new Error("Code challenge not found");
    }
  }

  renewTokenInIframe(url) {
    const iframe = this.createIframe(url);
    this.addIFrameToBody(iframe);
    return this.monitorQueryParamResponse(iframe.contentWindow)
      .then((queryParamMap) => {
        return this.handleQueryParamsResponse(queryParamMap).toPromise();
      })
      .finally(() => {
        this.removeHiddenIframe(iframe);
      });
  }

  pkceAuthorization() {
    const verifier = this.generateVerifier();
    this.storeVerifier(verifier);
    return this.createCodeChallenge(verifier).then((codeChallenge) => {
      this.storeCodeChallenge(codeChallenge);
      window.location.href = this.createPkceAuthorizationRequestUrl(
        codeChallenge
      );
    });
  }

  storeVerifier(verifier) {
    return this.authService.storeVerifier(verifier);
  }

  handleQueryParamsResponse(queryParamsMap) {
    if (queryParamsMap.has("code")) {
      return this.handleCodeResponse(queryParamsMap.get("code"));
    } else if (queryParamsMap.has("error")) {
      return throwError(queryParamsMap.get("error"));
    }
  }

  handleRefreshTokenAuthTokenError(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse
  ) {
    if (error instanceof HttpErrorResponse) {
      return this.handleRefreshTokenAuthHttpErrorResponse(req, next, error);
    } else {
      return throwError(error);
    }
  }

  handleSilentAuthTokenError(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<any> {
    if (error instanceof HttpErrorResponse) {
      return this.handleSilentAuthHttpErrorResponse(req, next, error);
    } else if (typeof error === "string") {
      switch (error) {
        case "authorize_response_timeout":
          this.clearAuthData();
          return from(this.pkceAuthorization()).pipe(
            mergeMap(() => {
              return throwError(error);
            })
          );
        case "interaction_required":
        case "login_required":
        case "consent_required":
          {
            const codeChallenge = this.loadCodeChallenge();
            if (codeChallenge) {
              window.location.href = this.createAuthorizationRequestUrl(
                codeChallenge
              );
            } else {
              throw new Error("Code Challenge not found");
            }
          }
          return throwError(error);
        default: {
          return throwError(error);
        }
      }
    }
  }
  handleSilentAuthHttpErrorResponse(
    req,
    next,
    error: HttpErrorResponse
  ): Observable<any> {
    if (error.status === 401) {
      this.clearAuthData();
    }
    return throwError(error);
  }

  handleRefreshTokenAuthHttpErrorResponse(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<any> {
    if (error.status === 401) {
      this.clearAccessToken();
      this.clearAccessTokenStoreTime();
      this.clearTokenExpiresIn();
      if (this.refreshTokenInProgress) {
        return this.refreshTokenReplay.pipe(
          mergeMap(() => {
            return this.tokenState(req, next);
          }),
          catchError((e) => {
            return this.handleRefreshTokenAuthTokenError(req, next, error);
          })
        );
      } else {
        return this.refreshTokenState().pipe(
          mergeMap(() => {
            return this.tokenState(req, next);
          }),
          catchError((e) => {
            if (e instanceof HttpErrorResponse && e.status === 401) {
              return from(this.logout()).pipe(
                mergeMap(() => {
                  return throwError(e);
                })
              );
            } else {
              return throwError(e);
            }
          })
        );
      }
    } else {
      return throwError(error);
    }
  }

  logout() {
    return this.logoutService.logout();
  }

  createErrorUrl(code, message?) {
    if (message) {
      return this.router.parseUrl(`/error?code=${code}&message=${message}`);
    } else {
      return this.router.parseUrl(`/error?code=${code}`);
    }
  }

  navigateByUrl(url) {
    return this.router.navigateByUrl(url);
  }

  handleCodeResponse(code) {
    if (this.loadVerifier()) {
      return this.runPkceAuthorizationCodeExchange(
        code,
        this.loadVerifier()
      ).pipe(
        tap((data: any) => {
          if (data.access_token) {
            this.storeToken(data.access_token);
            this.storeTokenStoreTime(this.now());
          }
          if (data.expires_in) {
            this.storeExpiresIn(data.expires_in);
          }
          if (this.storeRefreshTokens && data.refresh_token) {
            this.storeRefreshToken(data.refresh_token);
          }
        })
      );
    } else {
      throw new Error("Verifier not found");
    }
  }

  loadVerifier() {
    return this.authService.loadVerifier();
  }

  runPkceAuthorizationCodeExchange(code, verifier) {
    return this.authService.runPkceAuthorizationCodeExchange(code, verifier);
  }

  storeAccessToken(access_token) {
    this.authService.storeToken(access_token);
  }

  monitorQueryParamResponse(contentWindow: Window): Promise<ParamMap> {
    return new Promise((resolve, reject) => {
      let url;
      let ticks = 0;

      const timeout = 6000;
      const POLLING_INTERVAL_MS = 50;
      const maxTicks = timeout / POLLING_INTERVAL_MS;
      const intervalId = setInterval(() => {
        try {
          /*
           * Will throw if cross origin,
           * which should be caught and ignored
           * since we need the interval to keep running while on STS UI.
           */
          const href = contentWindow.location.href;
          url = href.replace(window.location.origin, "");
        } catch (e) {}

        ticks++;

        const queryParamMap = this.parseQueryParamsMap(url);
        const containsResponse = this.queryParamMapContainsAuthorizeResponse(
          queryParamMap
        );
        if (containsResponse) {
          clearInterval(intervalId);
          resolve(queryParamMap);
        } else if (ticks > maxTicks) {
          clearInterval(intervalId);
          reject("authorize_response_timeout");
        }
      });
    });
  }

  queryParamMapContainsAuthorizeResponse(paramMap: ParamMap) {
    const contains = this.authorizeResponseQueryParams.find((param) => {
      return paramMap.has(param);
    });
    return contains;
  }

  parseUrl(url) {
    return this.router.parseUrl(url);
  }

  parseQueryParamsMap(url) {
    const parsedUrl = this.parseUrl(url);
    return parsedUrl.queryParamMap;
  }

  createIframe(src): HTMLIFrameElement {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("id", "RenewToken");
    iframe.style.visibility = "hidden";
    iframe.style.position = "absolute";
    iframe.style.width = iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute(
      "sandbox",
      "allow-scripts allow-same-origin allow-forms"
    );
    iframe.src = src;
    return iframe;
  }

  removeHiddenIframe(iframe: HTMLIFrameElement) {
    document.body.removeChild(iframe);
  }

  addIFrameToBody(iframe) {
    return document
      .getElementsByTagName("body")[0]
      .appendChild(iframe) as HTMLIFrameElement;
  }

  loadCodeChallenge() {
    return this.authService.loadCodeChallenge();
  }

  generateVerifier() {
    return this.authService.generateVerifier();
  }

  createCodeChallenge(verifier) {
    return this.authService.createCodeChallenge(verifier);
  }

  storeCodeChallenge(code_challenge) {
    return this.authService.storeCodeChallenge(code_challenge);
  }

  loadToken() {
    return this.authService.loadToken();
  }

  loadExpiresIn() {
    return this.authService.loadExpiresIn();
  }

  loadTokenStoreTime() {
    return this.authService.loadTokenStoreTime();
  }

  createAuthorizationRequestUrl(codeChallenge, prompt?) {
    return this.authService.createPkceAuthorizationRequestUrl(
      codeChallenge,
      prompt
    );
  }
}
